import { useState, useEffect, useRef } from "react";
import type { Location, History } from "history";

interface UseHandleUrlParamConfig<T = any> {
  /** Query param name (e.g., 'billableTimeId', 'chat_id') */
  paramName: string;
  /** Sync callback when param is found (receives param value) */
  onParamFound?: (paramValue: string) => void;
  /** Async function to fetch data (receives param value) */
  fetchData?: (paramValue: string) => void;
  /** Data from async source (Redux/React Query/etc) */
  data?: T;
  /** Success flag for async data */
  isSuccess?: boolean;
  /** Callback when async data is ready (receives data) */
  onDataReady?: (data: T) => void;
  /** Optional function to validate data matches the param (prevents stale data) */
  validateData?: (data: T, paramValue: string) => boolean;
  /** React Router location object */
  location: Location;
  /** React Router history object (required if clearParam is true) */
  history?: History;
  /** Whether to clear the URL param after action (default: false) */
  clearParam?: boolean;
}

interface UseHandleUrlParamReturn {
  isLoading: boolean;
}

/**
 * hook to handle actions based on URL query parameters
 * Works with: Redux, React Query, plain state, or synchronous data
 */
export const useHandleUrlParam = <T = any>({
  paramName,
  onParamFound,
  fetchData,
  data,
  isSuccess,
  onDataReady,
  validateData,
  location,
  history,
  clearParam = false,
}: UseHandleUrlParamConfig<T>): UseHandleUrlParamReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const lastProcessedValue = useRef<string | null>(null);
  const shouldClearParam = useRef(false);
  const params = new URLSearchParams(location.search);
  const paramValue = params.get(paramName);

  const clearUrlParam = () => {
    if (!clearParam || !history) return;

    const newParams = new URLSearchParams(location.search);
    newParams.delete(paramName);
    const newSearch = newParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    history.replace(newUrl);
  };

  useEffect(() => {
    // Reset when param is cleared
    if (!paramValue) {
      if (lastProcessedValue.current) lastProcessedValue.current = null;
      return;
    }

    // Skip if already processed this value
    if (paramValue === lastProcessedValue.current) return;
    lastProcessedValue.current = paramValue;

    // Synchronous case: call immediately and clear
    if (onParamFound) {
      onParamFound(paramValue);
      clearUrlParam();
    }

    // Asynchronous case: trigger fetch and clear param after data ready
    if (fetchData) {
      setIsLoading(true);
      shouldClearParam.current = true;
      fetchData(paramValue);
    }
  }, [paramValue, onParamFound, fetchData, clearParam, history, location, paramName]);

  // async data ready
  useEffect(() => {
    if (!isLoading || !isSuccess || !data || !onDataReady) return;

    // Validate data matches the param we're waiting for (prevents stale data/race conditions)
    if (validateData && lastProcessedValue.current) {
      if (!validateData(data, lastProcessedValue.current)) {
        return;
      }
    }

    setIsLoading(false);
    onDataReady(data);

    if (shouldClearParam.current) {
      shouldClearParam.current = false;
      clearUrlParam();
    }
  }, [
    isLoading,
    isSuccess,
    data,
    onDataReady,
    validateData,
    clearParam,
    history,
    location,
    paramName,
  ]);

  return { isLoading };
};
